/**
 * useSlotSignatures — Composer 서명 슬롯 수집 훅(P2, 인증 작성 페이지 전용).
 *
 * 흐름(설계 §0 재정향 — 직접 패드만): 작성 폼의 서명 칸 탭 → 그 행 인물 이름이 뜬 서명 패드
 * 모달 → PUT /reports/:id/slot-signatures → **응답이 돌려준 서버 정본으로 셀 반영** → 모달 닫힘.
 *
 * ⚠️ 표시 맵(signatures)에는 **서버가 확정해 돌려준 값만** 앉힌다 — 패드에서 그린 데이터를
 * 로컬로 밀어 넣지 않는다. 그리면 "화면엔 서명이 있는데 서버엔 없는"(저장 실패 은폐) 상태가
 * 된다(contactSign 수집 후 강제 재fetch 선례와 같은 규약 — ComposerReportDetail).
 *
 * 금지되는 것은 **그린 데이터의 로컬 반영**이지 응답의 반영이 아니다(2026-08-30 W12 정정).
 * PUT은 저장된 행(`ReportSlotSignatureView` — 서명자·시각까지 서버가 확정한 값)을 그대로
 * 돌려주므로, 그 한 건을 맵에 머지하면 전체 GET을 다시 던질 이유가 없다. 서명 한 건마다
 * 맵 전체(서명 이미지 전량)를 재조회하는 것은 칸이 늘수록 제곱으로 비싸진다.
 * 삭제도 같다 — 204를 받은 뒤 그 주소를 맵에서 지우는 것은 실패를 은폐할 수 없다
 * (실패는 throw로 온다).
 *
 * reportId가 없으면(생성 모드 — 아직 보고서가 없다) 전체가 미배선 degrade: signatureSlots가
 * undefined라 rc는 표시 전용 빈 칸을 그린다(서명은 저장된 보고서에서 걷는다 — 주소 (reportId,
 * slotKey, rowId)의 reportId가 없으면 증빙을 둘 곳이 없다).
 *
 * 삭제(S13 재수집)는 확인 후 실행 — deleteRequest를 노출하고 페이지가 ConfirmModal을 그린다
 * (작성 당시 web에 ConfirmProvider가 없어 이 형태를 택했다. 2026-10-30부터 Provider 없는 명령형 확인
 * `useConfirmDialog`(`@safetyhub/ui`)가 있다 — 이 훅은 바꾸지 않았다).
 *
 * 설계: docs/decisions/2026-08-28-row-signature-slot-design.md
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ComposerSignatureSlotsContext,
  SignatureSlotRef,
  SignatureSlotSignRequest,
} from '@safetyhub/report-composer';
import type { SlotSignatureRenderMap, SlotSignatureRenderView } from '@safetyhub/shared';
import { STANDALONE_SLOT_ROW_KEY } from '@safetyhub/shared';
import {
  deleteReportSlotSignature,
  getReportSlotSignatures,
  putReportSlotSignature,
  type ReportSlotSignatureMap,
} from '../lib/reports';
import { extractApiError } from '../lib/api';

/**
 * 서버 GET 맵(standalone/rows 분리) → rc 렌더 맵(slotKey→(rowId|''→뷰)).
 * 서버 listRenderMap(liveData 채널)과 같은 접기 — 작성폼과 상세가 같은 형상을 읽는다.
 */
export function toSlotSignatureRenderMap(map: ReportSlotSignatureMap): SlotSignatureRenderMap {
  const out: SlotSignatureRenderMap = {};
  for (const [slotKey, entry] of Object.entries(map)) {
    const slot: SlotSignatureRenderMap[string] = {};
    if (entry.standalone) slot[STANDALONE_SLOT_ROW_KEY] = entry.standalone;
    for (const [rowId, view] of Object.entries(entry.rows ?? {})) slot[rowId] = view;
    out[slotKey] = slot;
  }
  return out;
}

/** 슬롯 주소 한 건의 쓰기 결과 — `view: null`이면 삭제. */
interface SlotSignatureWrite {
  slotKey: string;
  rowKey: string;
  view: SlotSignatureRenderView | null;
}

/** 렌더 맵에 쓰기 한 건을 반영한 **새 맵**. 슬롯이 비면 키까지 걷어 낸다. */
function applyWrite(map: SlotSignatureRenderMap, w: SlotSignatureWrite): SlotSignatureRenderMap {
  const slot = { ...(map[w.slotKey] ?? {}) };
  if (w.view) slot[w.rowKey] = w.view;
  else delete slot[w.rowKey];
  const next = { ...map };
  if (Object.keys(slot).length > 0) next[w.slotKey] = slot;
  else delete next[w.slotKey];
  return next;
}

export interface UseSlotSignaturesOptions {
  /** 수집 대상 보고서 — 없으면(생성 모드) 미배선 degrade(표시 전용). */
  reportId?: string;
  companyId?: string;
  /** 실패 토스트 등 host 통지(선택). */
  onError?: (message: string) => void;
}

export interface UseSlotSignaturesResult {
  /**
   * rc 렌더러(ComposerWidgetTreeRenderer 등)에 그대로 넘기는 host 채널.
   * 미배선(reportId 부재)이면 undefined — rc가 표시 전용으로 degrade.
   */
  signatureSlots: ComposerSignatureSlotsContext | undefined;
  /** 열려 있는 서명 패드 대상(칸 탭 시 설정) — null이면 모달 닫힘. */
  signRequest: SignatureSlotSignRequest | null;
  isSigning: boolean;
  signError: string | undefined;
  closeSignModal: () => void;
  /** 서명 패드 확정 — PUT 후 서버 재조회로 셀 반영. 실패 시 모달 유지 + signError. */
  submitSignature: (signatureData: string) => Promise<void>;
  /** 지우기(×) 확인 대기 대상 — 페이지가 ConfirmModal로 승인받아 confirmDelete를 부른다. */
  deleteRequest: SignatureSlotRef | null;
  isDeleting: boolean;
  confirmDelete: () => Promise<void>;
  cancelDelete: () => void;
}

export function useSlotSignatures({
  reportId,
  companyId,
  onError,
}: UseSlotSignaturesOptions): UseSlotSignaturesResult {
  const enabled = !!reportId && !!companyId;
  const [signatures, setSignatures] = useState<SlotSignatureRenderMap>({});
  const [signRequest, setSignRequest] = useState<SignatureSlotSignRequest | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [signError, setSignError] = useState<string | undefined>(undefined);
  const [deleteRequest, setDeleteRequest] = useState<SignatureSlotRef | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // 언마운트 후 setState 방지(연속 재조회 경합 포함) — 최신 요청만 반영.
  const fetchSeqRef = useRef(0);

  /**
   * 조회가 도는 **동안** 일어난 쓰기 — 그 조회 결과 위에 다시 얹는다.
   *
   * ⚠️ 이게 없으면 초기 로드 중에 서명한 사용자가 둘 중 하나를 잃는다: 응답을 버리면
   * *다른 행의 기존 서명*이 영영 안 뜨고, 응답을 그대로 앉히면 *방금 저장한 서명*이
   * 사라져 저장 실패처럼 보인다. 조회는 쓰기 **이전** 스냅샷이므로 위에 덧대는 것이 맞다.
   */
  const pendingWritesRef = useRef<SlotSignatureWrite[]>([]);
  const isFetchingRef = useRef(false);

  /**
   * 쓰기 응답 한 건을 표시 맵에 앉힌다(W12) — 전체 재조회 대신.
   * `view`가 null이면 그 주소를 지운다(삭제).
   */
  const applySlotSignature = useCallback(
    (slotKey: string, rowId: string | undefined, view: SlotSignatureRenderView | null) => {
      const write: SlotSignatureWrite = { slotKey, rowKey: rowId || STANDALONE_SLOT_ROW_KEY, view };
      if (isFetchingRef.current) pendingWritesRef.current.push(write);
      setSignatures((prev) => applyWrite(prev, write));
    },
    [],
  );

  const refetch = useCallback(async (): Promise<void> => {
    if (!reportId || !companyId) return;
    const seq = ++fetchSeqRef.current;
    isFetchingRef.current = true;
    pendingWritesRef.current = [];
    try {
      const map = await getReportSlotSignatures(reportId, companyId);
      if (seq === fetchSeqRef.current) {
        // 조회가 도는 사이의 쓰기를 응답 위에 다시 얹는다(위 ref 주석).
        setSignatures(
          pendingWritesRef.current.reduce(applyWrite, toSlotSignatureRenderMap(map)),
        );
      }
    } catch {
      // 조회 실패는 비차단(빈 칸 표시 유지) — 쓰기 경로 실패만 사용자에게 알린다.
    } finally {
      // ⚠️ **내 시퀀스일 때만** 정리한다. 겹친 재조회(대상 변경 cleanup·연속 refetch)에서 옛 조회가
      // 나중에 끝나면 여기서 **새 조회의** 진행 중 표시·대기 쓰기를 지워, 새 조회가 그 사이의 서명을
      // 응답 위에 못 얹고 잃는다 — 위 ref 주석이 막으려던 바로 그 손실(2026-08-30 리뷰 INFO).
      if (seq === fetchSeqRef.current) {
        isFetchingRef.current = false;
        pendingWritesRef.current = [];
      }
    }
  }, [reportId, companyId]);

  // 편집 진입 시 기존 서명 로드(재진입 표시 — S13 재수집의 전제).
  useEffect(() => {
    if (!enabled) return;
    void refetch();
    return () => {
      // 대상이 바뀌면(다른 보고서) 이전 응답이 새 화면에 앉지 않게 시퀀스를 무효화. 무효화된 조회는
      // 자기 `finally`에서 손을 떼므로(seq 가드) 진행 중 표시·대기 쓰기는 여기서 함께 접는다 —
      // 다음 refetch가 시작하며 다시 세운다.
      fetchSeqRef.current += 1;
      isFetchingRef.current = false;
      pendingWritesRef.current = [];
    };
  }, [enabled, refetch]);

  const submitSignature = useCallback(
    async (signatureData: string): Promise<void> => {
      if (!reportId || !companyId || !signRequest) return;
      setIsSigning(true);
      setSignError(undefined);
      try {
        const saved = await putReportSlotSignature(reportId, companyId, {
          slotKey: signRequest.slotKey,
          rowId: signRequest.rowId,
          // 서명자 표시명 snapshot — 행 이름이 비어 있으면 익명 표시(서버 DTO가 1자 이상 요구).
          signerName: signRequest.signerName?.trim() || '서명자',
          signatureData,
        });
        // 셀 반영은 **응답이 돌려준 서버 정본**으로 — 그린 데이터를 앉히지 않는다(위 모듈 doc).
        // 전체 GET을 다시 던지지 않는 이유도 같은 문단에 있다.
        applySlotSignature(signRequest.slotKey, signRequest.rowId, saved);
        setSignRequest(null);
      } catch (err) {
        const message = extractApiError(err).message || '서명 저장에 실패했습니다.';
        setSignError(message);
        onError?.(message);
      } finally {
        setIsSigning(false);
      }
    },
    [reportId, companyId, signRequest, applySlotSignature, onError],
  );

  const confirmDelete = useCallback(async (): Promise<void> => {
    if (!reportId || !companyId || !deleteRequest) return;
    setIsDeleting(true);
    try {
      await deleteReportSlotSignature(
        reportId,
        companyId,
        deleteRequest.slotKey,
        deleteRequest.rowId,
      );
      // 삭제 응답은 `data: null`이라 앉힐 값이 없다 — 성공한 주소를 맵에서 걷어 낸다.
      // (실패는 throw로 오므로 이 지움이 실패를 은폐하지 않는다.)
      applySlotSignature(deleteRequest.slotKey, deleteRequest.rowId, null);
      setDeleteRequest(null);
    } catch (err) {
      const message = extractApiError(err).message || '서명 삭제에 실패했습니다.';
      onError?.(message);
      setDeleteRequest(null);
    } finally {
      setIsDeleting(false);
    }
  }, [reportId, companyId, deleteRequest, applySlotSignature, onError]);

  const signatureSlots = useMemo<ComposerSignatureSlotsContext | undefined>(() => {
    if (!enabled) return undefined;
    return {
      signatures,
      onRequestSign: (req) => {
        setSignError(undefined);
        setSignRequest(req);
      },
      onRequestDelete: (req) => setDeleteRequest(req),
    };
  }, [enabled, signatures]);

  return {
    signatureSlots,
    signRequest,
    isSigning,
    signError,
    closeSignModal: () => {
      setSignRequest(null);
      setSignError(undefined);
    },
    submitSignature,
    deleteRequest,
    isDeleting,
    confirmDelete,
    cancelDelete: () => setDeleteRequest(null),
  };
}
